/**
 * Alerts Option A waveform control: pick a library message for this camera,
 * preview it, play it on the speaker (ENG-2232), or record a new one.
 */
import React from "react";
import {
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  describeTalkDownErrorDetail,
  describeTalkDownOutcome,
  describeTalkDownOutcomeDetail,
  describeTalkDownSendError,
  pollTalkDownOutcome,
  TALK_DOWN_SENT_MESSAGE,
} from "../../common/TalkDown/talkDownApi";
import { noteTalkDownErrorCode } from "../../common/TalkDown/talkDownCapabilityStore";
import { AddTalkDownMessageWizard } from "../TalkDownMessages/AddTalkDownMessageWizard";
import {
  CameraTalkDownMessage,
  listTalkDownMessagesForCamera,
  playTalkDownMessageOnCamera,
} from "../TalkDownMessages/talkDownMessagesApi";
import { formatTalkDownDuration } from "../TalkDownMessages/talkDownMessageTypes";

const talkDownIconButtonClassName =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border-[1.5px] border-[#cfd8e6] bg-white text-[#233762]";

const WaveformIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 10v3" />
    <path d="M6 6v11" />
    <path d="M10 3v18" />
    <path d="M14 8v7" />
    <path d="M18 5v13" />
    <path d="M22 10v3" />
  </svg>
);

type PlayPhase = "idle" | "sending" | "waiting" | "played" | "failed" | "error";

type PlayState = {
  phase: PlayPhase;
  message: string;
};

const IDLE_PLAY: PlayState = { phase: "idle", message: "" };

/** Per-camera cache for the session (ENG-2232: temporary UI state). */
const messagesCache = new Map<string, CameraTalkDownMessage[]>();

/** Test helper — clears the in-memory camera message cache. */
export const clearTalkDownLibraryMessagesCache = () => {
  messagesCache.clear();
};

export const useAlertTalkDownLibrary = (
  cameraId: string | number | null | undefined,
  cameraName: string,
  parentGroupId?: string | number | null,
  siteId?: string | number | null,
  siteName?: string | null
) => {
  const cameraKey =
    cameraId == null || cameraId === "" ? null : String(cameraId);

  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<CameraTalkDownMessage[]>([]);
  const [selected, setSelected] = React.useState<CameraTalkDownMessage | null>(
    null
  );
  const [playState, setPlayState] = React.useState<PlayState>(IDLE_PLAY);
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setSelected(null);
    setPlayState(IDLE_PLAY);
    setMenuAnchor(null);
    setLoadError(null);
    if (cameraKey && messagesCache.has(cameraKey)) {
      setMessages(messagesCache.get(cameraKey)!);
    } else {
      setMessages([]);
    }
  }, [cameraKey]);

  React.useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const loadMessages = React.useCallback(
    async (force = false) => {
      if (!cameraKey) {
        setMessages([]);
        setLoadError("No camera selected.");
        setLoading(false);
        return;
      }
      if (!force && messagesCache.has(cameraKey)) {
        setMessages(messagesCache.get(cameraKey)!);
        setLoading(false);
        setLoadError(null);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const rows = await listTalkDownMessagesForCamera(cameraKey);
        messagesCache.set(cameraKey, rows);
        setMessages(rows);
      } catch {
        setLoadError("Could not load messages for this camera.");
        setMessages([]);
      } finally {
        setLoading(false);
      }
    },
    [cameraKey]
  );

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    void loadMessages(false);
  };

  const closeMenu = () => setMenuAnchor(null);

  const invalidateCache = React.useCallback(() => {
    if (cameraKey) messagesCache.delete(cameraKey);
  }, [cameraKey]);

  const selectMessage = (message: CameraTalkDownMessage) => {
    setSelected(message);
    setPlayState(IDLE_PLAY);
    closeMenu();
  };

  const playBusy =
    playState.phase === "sending" || playState.phase === "waiting";

  const handlePlayOnCamera = async () => {
    if (!cameraKey || !selected) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const live = () =>
      abortRef.current === controller && !controller.signal.aborted;

    setPlayState({ phase: "sending", message: "Sending..." });
    try {
      const dispatch = await playTalkDownMessageOnCamera(
        cameraKey,
        selected.id
      );
      if (!live()) return;
      setPlayState({ phase: "waiting", message: TALK_DOWN_SENT_MESSAGE });
      const result = await pollTalkDownOutcome(
        dispatch.site_id,
        dispatch.command_id,
        { signal: controller.signal }
      );
      if (!live() || result.kind === "aborted") return;
      if (result.kind === "not_received") {
        setPlayState({
          phase: "failed",
          message: "Couldn't confirm the talk-down was received",
        });
        return;
      }
      const row = result.kind === "done" ? result.row : null;
      const outcome = describeTalkDownOutcome(row, cameraName);
      setPlayState({
        phase: row == null ? "failed" : outcome.ok ? "played" : "failed",
        message: outcome.message,
      });
      noteTalkDownErrorCode(cameraKey, row?.result?.error_code);
      invalidateCache();
      void loadMessages(true);
    } catch (reason) {
      if (!live()) return;
      setPlayState({
        phase: "error",
        message: describeTalkDownSendError(reason),
      });
      // Keep detail available for staff debugging via console.
      const detail = describeTalkDownErrorDetail(reason);
      if (detail) {
        // eslint-disable-next-line no-console
        console.warn("[talk-down library play]", detail);
      }
    }
  };

  const button = (
    <>
      <button
        type="button"
        aria-label="Play pre-recorded message"
        title="Play pre-recorded message"
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor)}
        className={talkDownIconButtonClassName}
        onClick={openMenu}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <WaveformIcon />
      </button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        sx={{ zIndex: 10000 }}
        PaperProps={{
          sx: {
            minWidth: 260,
            maxWidth: 320,
            maxHeight: 360,
            fontFamily: "Mulish",
          },
        }}
        MenuListProps={{ "aria-label": "Pre-recorded talk-down messages" }}
      >
        {loading && (
          <MenuItem disabled sx={{ fontFamily: "Mulish", gap: 1.5 }}>
            <CircularProgress size={18} sx={{ color: "#FF8400" }} />
            Loading messages…
          </MenuItem>
        )}
        {!loading && loadError && (
          <MenuItem disabled sx={{ fontFamily: "Mulish", color: "#B3261E" }}>
            {loadError}
          </MenuItem>
        )}
        {!loading &&
          !loadError &&
          messages.map((m) => (
            <MenuItem
              key={m.id}
              onClick={() => selectMessage(m)}
              selected={selected?.id === m.id}
              sx={{
                fontFamily: "Mulish",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={m.name}
            >
              {m.name}
              <span className="ml-2 text-[12px] text-gray-500">
                {formatTalkDownDuration(m.durationSeconds)}
              </span>
            </MenuItem>
          ))}
        {!loading && !loadError && !messages.length && (
          <MenuItem disabled sx={{ fontFamily: "Mulish", color: "#6b7280" }}>
            No messages for this camera
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            closeMenu();
            setWizardOpen(true);
          }}
          sx={{
            fontFamily: "Mulish",
            color: "#FF8400",
            fontWeight: 700,
          }}
        >
          Record new message
        </MenuItem>
      </Menu>

      <AddTalkDownMessageWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialGroupId={parentGroupId}
        defaultAssignment={
          cameraKey && siteId != null && siteId !== ""
            ? {
                siteId,
                siteName: siteName || "Site",
                mode: "cameras",
                cameraIds: [cameraKey],
                cameraNames: cameraName ? [cameraName] : [],
              }
            : null
        }
        onSaved={() => {
          invalidateCache();
          void loadMessages(true);
        }}
      />
    </>
  );

  const panel =
    selected == null ? null : (
      <div
        data-testid="alert-talk-down-library-panel"
        className="mb-3 w-full rounded-[10px] border-[1.5px] border-[#cfd8e6] bg-white px-4 py-3 font-mulish text-[#20283a]"
      >
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            <p className="text-[15px] font-semibold text-actuate-blue">
              {selected.name}
            </p>
            <p className="text-[12px] text-gray-500">
              {formatTalkDownDuration(selected.durationSeconds)}
              {selected.recordedBy ? ` · ${selected.recordedBy}` : ""}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close selected message"
            className="text-[13px] text-gray-500 underline hover:text-actuate-blue"
            onClick={() => {
              setSelected(null);
              setPlayState(IDLE_PLAY);
            }}
          >
            Close
          </button>
        </div>
        {selected.audioUrl ? (
          <audio
            controls
            src={selected.audioUrl}
            className="mb-3 h-10 w-full"
            data-testid="alert-talk-down-library-preview"
          />
        ) : (
          <p className="mb-3 text-[13px] text-gray-500">
            No audio available to preview.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <OrangeTextButton
            text={playBusy ? "Playing…" : "Play this now on camera"}
            disabled={playBusy || !selected.audioUrl}
            onClick={() => void handlePlayOnCamera()}
          />
          {playState.phase !== "idle" && (
            <span
              className={`text-[13px] ${
                playState.phase === "error" || playState.phase === "failed"
                  ? "text-[#B3261E]"
                  : playState.phase === "played"
                  ? "text-[#1B7F3B]"
                  : "text-gray-600"
              }`}
            >
              {playState.message}
            </span>
          )}
        </div>
      </div>
    );

  return { button, panel, hasSelection: Boolean(selected) };
};
